import { AlertCircle, XCircle, AlertTriangle, Info } from 'lucide-react';

const iconMap = {
  'alert-circle': AlertCircle,
  'x-circle': XCircle,
  'alert-triangle': AlertTriangle,
  info: Info,
} as const;

type SupportedIconName = keyof typeof iconMap;
type ErrorType = string | string[] | null;

interface FormErrorProps {
  readonly error: ErrorType;
  readonly icon?: SupportedIconName;
  readonly className?: string;
}

const isArrayOfErrors = (error: ErrorType): error is string[] => {
  return Array.isArray(error) && error.length > 0;
};

const isSingleError = (error: ErrorType): error is string => {
  return typeof error === 'string' && error.length > 0;
};

const FormError = ({ error, icon, className = '' }: FormErrorProps) => {
  if (!error || (!isArrayOfErrors(error) && !isSingleError(error))) return null;

  const renderIcon = () => {
    if (!icon || !iconMap[icon]) return null;

    const IconComponent = iconMap[icon];
    return <IconComponent aria-hidden="true" className="text-red-800 w-4 h-4 mt-0.5 flex-shrink-0" />;
  };

  const renderSingleError = (errorMessage: string) => <div className="text-red-800 text-sm">{errorMessage}</div>;

  const renderMultipleErrors = (errors: string[]) => (
    <div className="text-red-800 text-sm space-y-1">
      {errors.map((errorMessage) => (
        <div key={errorMessage}>{errorMessage}</div>
      ))}
    </div>
  );

  const content = isArrayOfErrors(error) ? renderMultipleErrors(error) : renderSingleError(error);

  return (
    <div className={`bg-red-50 border border-red-200 rounded-md p-4 ${className}`} role="alert">
      <div className={`flex ${icon ? 'items-start gap-2' : ''}`}>
        {renderIcon()}
        {content}
      </div>
    </div>
  );
};

export type { ErrorType, FormErrorProps, SupportedIconName };
export { FormError };
